/**
 * DPDP Consent Banner (Sprint 86 — DevOps).
 *
 * Renders a sticky bottom banner the first time a user visits, capturing
 * explicit acceptance of:
 *   • Privacy policy (required to use the app)
 *   • Marketing notifications (opt-in, default OFF)
 *   • Tracking cookies (opt-in, default OFF)
 *
 * Backend persists every decision to db.consent_log via POST /api/consent
 * for audit-trail compliance with DPDP Act 2023 §6.
 *
 * Banner re-appears whenever PRIVACY_POLICY_VERSION on the backend bumps,
 * so policy changes always require a fresh acceptance click.
 */
import { useEffect, useState } from "react";
import { Shield } from "lucide-react";
import api from "../services/api";

const LOCAL_KEY = "pickngo_consent_v";

export default function ConsentBanner() {
  const [open, setOpen] = useState(false);
  const [needsAccept, setNeedsAccept] = useState(false);
  const [version, setVersion] = useState(null);
  const [marketing, setMarketing] = useState(false);
  const [tracking, setTracking] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/consent/status");
        if (cancelled) return;
        setVersion(data.current_policy_version);
        // Check local cache first (faster UX on repeat visits)
        const localAcceptedVersion = localStorage.getItem(LOCAL_KEY);
        if (localAcceptedVersion === data.current_policy_version && !data.needs_privacy_acceptance) {
          setOpen(false);
          return;
        }
        setNeedsAccept(Boolean(data.needs_privacy_acceptance));
        setOpen(Boolean(data.needs_privacy_acceptance));
      } catch {
        // Backend down → don't block app, just don't show banner.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const recordOne = async (consent_type, accepted) => {
    try {
      await api.post("/consent", { consent_type, accepted, version });
    } catch {
      // Best-effort; banner UX shouldn't break if backend hiccups.
    }
  };

  const acceptAll = async () => {
    await Promise.all([
      recordOne("privacy_policy", true),
      recordOne("data_processing", true),
      recordOne("marketing", marketing),
      recordOne("tracking_cookies", tracking),
    ]);
    localStorage.setItem(LOCAL_KEY, version);
    setOpen(false);
  };

  const acceptEssentialOnly = async () => {
    await Promise.all([
      recordOne("privacy_policy", true),
      recordOne("data_processing", true),
      recordOne("marketing", false),
      recordOne("tracking_cookies", false),
    ]);
    localStorage.setItem(LOCAL_KEY, version);
    setOpen(false);
  };

  if (!open || !needsAccept) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[60] border-t border-slate-200 bg-white/95 backdrop-blur shadow-[0_-8px_24px_rgba(0,0,0,0.08)]"
      data-testid="consent-banner"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex flex-col gap-3">
        <div className="flex items-start gap-3">
          <div className="hidden sm:flex h-9 w-9 items-center justify-center rounded-lg bg-orange-50 text-[#FF6B00] shrink-0">
            <Shield className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-900">
              We respect your privacy
            </p>
            <p className="text-xs text-slate-600 mt-0.5">
              PickNGo processes your name, phone &amp; address only to deliver your parcels (DPDP Act 2023 §6 — &quot;data fiduciary&quot;).
              Choose what else we may do.{" "}
              <a href="/privacy" className="text-[#FF6B00] underline">Privacy policy</a>.
            </p>
            <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-xs">
              <label className="flex items-center gap-2 text-slate-700">
                <input type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} data-testid="consent-marketing" />
                Marketing WhatsApp / SMS
              </label>
              <label className="flex items-center gap-2 text-slate-700">
                <input type="checkbox" checked={tracking} onChange={(e) => setTracking(e.target.checked)} data-testid="consent-tracking" />
                Analytics &amp; tracking cookies
              </label>
            </div>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:justify-end">
          <button
            onClick={acceptEssentialOnly}
            className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50"
            data-testid="consent-essential-btn"
          >
            Essential only
          </button>
          <button
            onClick={acceptAll}
            className="px-4 py-2 rounded-lg bg-[#FF6B00] text-white text-sm font-semibold hover:bg-[#E55A00]"
            data-testid="consent-accept-btn"
          >
            Accept &amp; continue
          </button>
        </div>
      </div>
    </div>
  );
}
